import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import { UpgradeDialogSource } from "schemas/dashboard";

import { sanitizeInput } from "features/Auth/Onboarding/AboutForm/helpers";
import { fetcherAuth } from "utils/api";

import InstagramEditDialog from "./InstagramEditDialog";
import InstagramSetup from "./InstagramSetup";
import styles from "./styles";

interface InstagramConnectProps {
  open: boolean;
  onClose: () => void;
}

export default function InstagramConnect({
  open,
  onClose,
}: InstagramConnectProps) {
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { profile, setProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { setUpgradeDialogSource, subscription } =
    useContext(SubscriptionContext);

  const currentUsername = sanitizeInput(profile?.instagramUrl || "");

  const saveInstagramProfile = async (handle: string) => {
    if (!handle || !currentOrg?.id) return;

    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/onboarding`,
        "PUT",
        {},
        {
          instagramUrl: `https://instagram.com/${handle}`,
          page: "Instagram Sidebar",
        },
      );

      setProfile((prev) => ({
        ...prev,
        ...organizationProfile,
      }));

      setAlert("Instagram account updated successfully!", "success");
    } catch (error: any) {
      setAlert(error.message || "Failed to update Instagram account", "error");
    }
  };

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  return (
    <Dialog
      fullScreen={isMobileScreen}
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle variant="h5">Instagram Tagging Feature</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 1 }}>
          Creators who pitch using this feature see a{" "}
          <Box component="span" sx={{ color: "green.main", fontWeight: 600 }}>
            42% higher response rate
          </Box>
          .
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ my: 1, fontSize: "18px" }}>
            How It Works
          </Typography>
          <Link
            sx={{
              fontWeight: 600,
              color: "purple.main",
              textDecoration: "none",
              borderBottom: "1px solid",
              borderColor: "purple.main",
            }}
            href="https://www.loom.com/share/ec89eca235e34f38b68db2e2de3935b3"
            target="_blank"
          >
            Watch Tutorial
          </Link>
          <ol>
            <li>
              Tag{" "}
              <Box
                component="a"
                href="https://instagram.com/onbento"
                target="_blank"
                rel="noopener noreferrer"
                sx={styles.link}
              >
                @onbento
              </Box>{" "}
              in a creator's post
            </li>
            <li>
              We'll add the brands they've tagged to your list so you can pitch
              them
            </li>
            <li>
              If you use our Virtual Assistant, those brands will also be added
              to next week's upcoming pitches automatically.
            </li>
          </ol>
          📌{" "}
          {subscription?.isSubscribed
            ? "Because you're on a paid plan,"
            : "If you're on a paid plan,"}{" "}
          we won't respond so you can tag under the radar.
        </Box>
        {!subscription?.isSubscribed && (
          <Button
            sx={{ mb: 2, mt: -1 }}
            variant="contained"
            disableElevation
            size="small"
            onClick={() =>
              setUpgradeDialogSource(
                UpgradeDialogSource.instagram_connected_dialog,
              )
            }
          >
            Upgrade Now
          </Button>
        )}
        {profile?.instagramUrl ? (
          <Box sx={styles.accountSection}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Your Account is connected to this Instagram:
            </Typography>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs>
                <Box sx={styles.usernameDisplay}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    @{currentUsername}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs="auto">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleEditClick}
                  sx={styles.editButton}
                >
                  <Box
                    component="i"
                    className="fa-regular fa-edit"
                    sx={{ mr: 1 }}
                  />
                  Edit
                </Button>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <InstagramSetup closeDialog={onClose} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Close
        </Button>
      </DialogActions>

      <InstagramEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        currentUsername={currentUsername}
        onSave={saveInstagramProfile}
      />
    </Dialog>
  );
}
